import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { businessService, BusinessListing } from '../services/businessService';
import { MAIN_CATEGORIES, SUB_CATEGORIES, BUYER_TYPES } from '../utils/categories';
import { Timestamp, GeoPoint } from 'firebase/firestore';
import { upsertFranchiseListing, removeFranchiseListing } from '../services/franchiseService';

const AdminListingEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuthContext();
  const [listing, setListing] = useState<BusinessListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state - comprehensive form data
  const [formData, setFormData] = useState<Partial<BusinessListing>>({
    basic: {
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      subcategory: '',
      tags: [],
      highlights: [],
      seoTitle: '',
      seoUrl: '',
      buyerTypes: [],
      status: 'active',
      featured: false,
      verified: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      expiresAt: Timestamp.now(),
    },
    location: {
      address: '',
      city: '',
      province: '',
      postalCode: '',
      country: '',
      coordinates: new GeoPoint(0, 0),
      region: '',
      timezone: '',
    },
    financial: {
      askingPrice: 0,
      currency: 'CAD',
      annualRevenue: 0,
      annualCashFlow: 0,
      annualExpenses: 0,
      profitMargin: 0,
      growthRate: 0,
      pricePerRevenue: 0,
      pricePerCashFlow: 0,
      financingAvailable: false,
    },
    business: {
      established: new Date().getFullYear(),
      employees: { min: 0, max: 0 },
      squareFootage: 0,
      leaseTerms: '',
      reasonForSale: '',
      trainingIncluded: false,
      relocatable: false,
      workFromHome: false,
      absenteeOwnership: false,
      franchise: false,
    },
    media: {
      images: [],
      videos: [],
      documents: [],
      featuredImage: '',
    },
    owner: {
      userId: '',
      brokerId: '',
      contactInfo: {
        name: '',
        email: '',
        phone: '',
        preferredContact: 'email',
      },
      responseTime: 24,
    },
  });

  usePageTitle(listing ? `Edit Listing: ${listing.basic.title} | Admin` : 'Edit Listing | Admin');

  useEffect(() => {
    const loadListing = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const listingData = await businessService.getBusiness(id);
        if (listingData) {
          setListing(listingData);
          setFormData({
            basic: listingData.basic,
            location: listingData.location,
            financial: listingData.financial,
            business: listingData.business,
            media: listingData.media,
            owner: listingData.owner,
            search: listingData.search,
          });
        } else {
          setError('Listing not found');
        }
      } catch (error) {
        console.error('Error loading listing:', error);
        setError('Failed to load listing');
      } finally {
        setLoading(false);
      }
    };

    loadListing();
  }, [id]);

  // Check if user is admin
  const isAdmin = currentUser && userProfile?.profile.userType === 'admin';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Handle nested paths like "basic.title" or "owner.contactInfo.name" or "business.employees.min"
    const path = name.split('.');
    
    setFormData(prev => {
      // Create a deep copy using structuredClone or manual copying
      const newData: Partial<BusinessListing> = {
        ...prev,
        basic: prev.basic ? { ...prev.basic } : undefined,
        location: prev.location ? { ...prev.location } : undefined,
        financial: prev.financial ? { ...prev.financial } : undefined,
        business: prev.business ? { ...prev.business, employees: prev.business.employees ? { ...prev.business.employees } : { min: 0, max: 0 } } : undefined,
        media: prev.media ? { ...prev.media } : undefined,
        owner: prev.owner ? { 
          ...prev.owner, 
          contactInfo: prev.owner.contactInfo ? { ...prev.owner.contactInfo } : {
            name: '',
            email: '',
            phone: '',
            preferredContact: 'email' as const
          }
        } : undefined,
      };
      
      let current: any = newData;
      
      // Navigate to the parent object
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        } else {
          current[path[i]] = { ...current[path[i]] };
        }
        current = current[path[i]];
      }
      
      const lastKey = path[path.length - 1];
      if (type === 'checkbox') {
        current[lastKey] = checked;
      } else if (type === 'number') {
        current[lastKey] = value === '' ? 0 : parseFloat(value);
      } else {
        current[lastKey] = value;
      }
      
      return newData;
    });
  };

  const handleArrayChange = (section: string, field: string, value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section as keyof BusinessListing] as any),
        [field]: value,
      },
    }));
  };

  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.currentTarget;
      const value = input.value.trim();
      if (value && formData.basic?.tags && !formData.basic.tags.includes(value)) {
        handleArrayChange('basic', 'tags', [...formData.basic.tags, value]);
        input.value = '';
      }
    }
  };

  const removeTag = (tag: string) => {
    if (formData.basic?.tags) {
      handleArrayChange('basic', 'tags', formData.basic.tags.filter(t => t !== tag));
    }
  };

  const handleHighlightInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.currentTarget;
      const value = input.value.trim();
      if (value && formData.basic?.highlights && !formData.basic.highlights.includes(value)) {
        handleArrayChange('basic', 'highlights', [...formData.basic.highlights, value]);
        input.value = '';
      }
    }
  };

  const removeHighlight = (highlight: string) => {
    if (formData.basic?.highlights) {
      handleArrayChange('basic', 'highlights', formData.basic.highlights.filter(h => h !== highlight));
    }
  };

  const handleBuyerTypeToggle = (buyerType: string) => {
    if (!formData.basic?.buyerTypes) return;
    const current = formData.basic.buyerTypes;
    const updated = current.includes(buyerType)
      ? current.filter(bt => bt !== buyerType)
      : [...current, buyerType];
    handleArrayChange('basic', 'buyerTypes', updated);
  };

  const handleMediaArrayChange = (type: 'images' | 'videos' | 'documents', value: string[]) => {
    setFormData(prev => ({
      ...prev,
      media: {
        ...prev.media!,
        [type]: value,
      },
    }));
  };

  const handleMediaInput = (type: 'images' | 'videos' | 'documents', e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.currentTarget;
      const value = input.value.trim();
      if (value && formData.media?.[type] && !formData.media[type].includes(value)) {
        handleMediaArrayChange(type, [...formData.media[type], value]);
        input.value = '';
      }
    }
  };

  const removeMediaItem = (type: 'images' | 'videos' | 'documents', item: string) => {
    if (formData.media?.[type]) {
      handleMediaArrayChange(type, formData.media[type].filter(i => i !== item));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listing) return;

    try {
      setSaving(true);
      setError(null);

      // Prepare update data - preserve all existing fields and update with form data
      const updateData: Partial<BusinessListing> = {
        basic: {
          ...listing.basic,
          ...formData.basic,
          updatedAt: Timestamp.now(),
        },
        location: {
          ...listing.location,
          ...formData.location,
        },
        financial: {
          ...listing.financial,
          ...formData.financial,
        },
        business: {
          ...listing.business,
          ...formData.business,
        },
        media: {
          ...listing.media,
          ...formData.media,
        },
        owner: {
          ...listing.owner,
          ...formData.owner,
        },
      };

      if (formData.search) {
        updateData.search = formData.search;
      }

      await businessService.updateBusiness(listing.id!, updateData);

      // Sync franchise collection when franchise flag changes
      try {
        const source = listing.id!.startsWith('ddf-') ? 'ddfListings' as const : 'businesses' as const;
        const listingId = listing.id!.startsWith('ddf-') ? listing.id!.replace('ddf-', '') : listing.id!;
        const isFranchise = (updateData.business as any)?.franchise ?? (listing.business as any)?.franchise;
        const merged = { basic: updateData.basic ?? listing.basic, location: updateData.location ?? listing.location, financial: updateData.financial ?? listing.financial, media: updateData.media ?? listing.media };
        if (isFranchise) {
          await upsertFranchiseListing(source, listingId, {
            title: (merged.basic as any)?.title ?? '',
            description: (merged.basic as any)?.description ?? '',
            category: (merged.basic as any)?.category ?? '',
            subcategory: (merged.basic as any)?.subcategory ?? '',
            province: (merged.location as any)?.province ?? '',
            city: (merged.location as any)?.city ?? '',
            askingPrice: (merged.financial as any)?.askingPrice ?? 0,
            currency: (merged.financial as any)?.currency ?? 'CAD',
            annualRevenue: (merged.financial as any)?.annualRevenue ?? 0,
            annualCashFlow: (merged.financial as any)?.annualCashFlow ?? 0,
            featuredImage: (merged.media as any)?.featuredImage ?? (merged.media as any)?.images?.[0] ?? '',
            status: (merged.basic as any)?.status ?? 'active',
            createdAt: (merged.basic as any)?.createdAt ?? Timestamp.now(),
            updatedAt: Timestamp.now(),
            tags: (merged.basic as any)?.tags,
            seoUrl: (merged.basic as any)?.seoUrl,
          });
        } else {
          await removeFranchiseListing(source, listingId);
        }
      } catch (franchiseErr) {
        console.warn('Franchise collection sync failed (non-blocking):', franchiseErr);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`);
      }, 2000);

    } catch (error) {
      console.error('Error updating listing:', error);
      setError('Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Access Denied</div>
          <p className="text-gray-600 mb-4">You don't have permission to access the admin area.</p>
          <Link to="/" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading listing details...</p>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">❌ Error</div>
          <p className="text-gray-600 mb-4">{error || 'Listing not found'}</p>
          <Link to="/admin/listings" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Back to Listings
          </Link>
        </div>
      </div>
    );
  }

  const formatTimestamp = (ts?: Timestamp) => {
    if (!ts) return 'N/A';
    return new Date(ts.toMillis()).toLocaleString();
  };

  const subcategories = formData.basic?.category ? (SUB_CATEGORIES[formData.basic.category] || []) : [];

  return (
    <>
      <Helmet>
        <title>Edit Listing: {listing.basic.title} | Admin</title>
        <meta name="description" content="Edit listing in admin dashboard" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link
                  to={listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Listing
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Edit Listing</h1>
                  <p className="text-gray-600 mt-1">Update listing information and settings</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  to="/admin/listings"
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Back to Listings
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center">
                <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-green-800 text-sm font-medium">
                  Listing updated successfully! Redirecting...
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="basic.title" className="block text-sm font-medium text-gray-700 mb-1">
                    Listing Title *
                  </label>
                  <input
                    type="text"
                    id="basic.title"
                    name="basic.title"
                    value={formData.basic?.title || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="basic.shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Short Description *
                  </label>
                  <textarea
                    id="basic.shortDescription"
                    name="basic.shortDescription"
                    value={formData.basic?.shortDescription || ''}
                    onChange={handleInputChange}
                    required
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="basic.description" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Description *
                  </label>
                  <textarea
                    id="basic.description"
                    name="basic.description"
                    value={formData.basic?.description || ''}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="basic.category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    id="basic.category"
                    name="basic.category"
                    value={formData.basic?.category || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select Category</option>
                    {MAIN_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="basic.subcategory" className="block text-sm font-medium text-gray-700 mb-1">
                    Subcategory *
                  </label>
                  <select
                    id="basic.subcategory"
                    name="basic.subcategory"
                    value={formData.basic?.subcategory || ''}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.basic?.category}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                  >
                    <option value="">Select Subcategory</option>
                    {subcategories.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="basic.status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    id="basic.status"
                    name="basic.status"
                    value={formData.basic?.status || 'active'}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="sold">Sold</option>
                    <option value="expired">Expired</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="basic.featured"
                      name="basic.featured"
                      checked={formData.basic?.featured || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="basic.featured" className="ml-2 block text-sm text-gray-900">
                      Featured Listing
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="basic.verified"
                      name="basic.verified"
                      checked={formData.basic?.verified || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="basic.verified" className="ml-2 block text-sm text-gray-900">
                      Verified Listing
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="basic.seoTitle" className="block text-sm font-medium text-gray-700 mb-1">
                    SEO Title
                  </label>
                  <input
                    type="text"
                    id="basic.seoTitle"
                    name="basic.seoTitle"
                    value={formData.basic?.seoTitle || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="basic.seoUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    SEO URL
                  </label>
                  <input
                    type="text"
                    id="basic.seoUrl"
                    name="basic.seoUrl"
                    value={formData.basic?.seoUrl || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    placeholder="Press Enter to add a tag"
                    onKeyDown={handleTagInput}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.basic?.tags?.map(tag => (
                      <span key={tag} className="inline-flex items-center px-2 py-1 rounded-md bg-primary-100 text-primary-800 text-sm">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-primary-600 hover:text-primary-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Highlights
                  </label>
                  <input
                    type="text"
                    placeholder="Press Enter to add a highlight"
                    onKeyDown={handleHighlightInput}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="mt-2 space-y-1">
                    {formData.basic?.highlights?.map(highlight => (
                      <div key={highlight} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded">
                        <span className="text-sm">{highlight}</span>
                        <button
                          type="button"
                          onClick={() => removeHighlight(highlight)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buyer Types
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {BUYER_TYPES.map(type => (
                      <label key={type} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.basic?.buyerTypes?.includes(type) || false}
                          onChange={() => handleBuyerTypeToggle(type)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="location.address" className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    id="location.address"
                    name="location.address"
                    value={formData.location?.address || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="location.city" className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    id="location.city"
                    name="location.city"
                    value={formData.location?.city || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="location.province" className="block text-sm font-medium text-gray-700 mb-1">
                    Province *
                  </label>
                  <input
                    type="text"
                    id="location.province"
                    name="location.province"
                    value={formData.location?.province || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="location.postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    id="location.postalCode"
                    name="location.postalCode"
                    value={formData.location?.postalCode || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="location.country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <input
                    type="text"
                    id="location.country"
                    name="location.country"
                    value={formData.location?.country || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="location.region" className="block text-sm font-medium text-gray-700 mb-1">
                    Region
                  </label>
                  <input
                    type="text"
                    id="location.region"
                    name="location.region"
                    value={formData.location?.region || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="location.timezone" className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <input
                    type="text"
                    id="location.timezone"
                    name="location.timezone"
                    value={formData.location?.timezone || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coordinates (Latitude, Longitude)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      step="any"
                      placeholder="Latitude"
                      value={formData.location?.coordinates?.latitude || ''}
                      onChange={(e) => {
                        const lat = parseFloat(e.target.value) || 0;
                        const lng = formData.location?.coordinates?.longitude || 0;
                        setFormData(prev => ({
                          ...prev,
                          location: {
                            ...prev.location!,
                            coordinates: new GeoPoint(lat, lng),
                          },
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input
                      type="number"
                      step="any"
                      placeholder="Longitude"
                      value={formData.location?.coordinates?.longitude || ''}
                      onChange={(e) => {
                        const lat = formData.location?.coordinates?.latitude || 0;
                        const lng = parseFloat(e.target.value) || 0;
                        setFormData(prev => ({
                          ...prev,
                          location: {
                            ...prev.location!,
                            coordinates: new GeoPoint(lat, lng),
                          },
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="financial.askingPrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Asking Price *
                  </label>
                  <input
                    type="number"
                    id="financial.askingPrice"
                    name="financial.askingPrice"
                    value={formData.financial?.askingPrice || 0}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="financial.currency" className="block text-sm font-medium text-gray-700 mb-1">
                    Currency *
                  </label>
                  <select
                    id="financial.currency"
                    name="financial.currency"
                    value={formData.financial?.currency || 'CAD'}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="financial.annualRevenue" className="block text-sm font-medium text-gray-700 mb-1">
                    Annual Revenue
                  </label>
                  <input
                    type="number"
                    id="financial.annualRevenue"
                    name="financial.annualRevenue"
                    value={formData.financial?.annualRevenue || 0}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="financial.annualCashFlow" className="block text-sm font-medium text-gray-700 mb-1">
                    Annual Cash Flow
                  </label>
                  <input
                    type="number"
                    id="financial.annualCashFlow"
                    name="financial.annualCashFlow"
                    value={formData.financial?.annualCashFlow || 0}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="financial.annualExpenses" className="block text-sm font-medium text-gray-700 mb-1">
                    Annual Expenses
                  </label>
                  <input
                    type="number"
                    id="financial.annualExpenses"
                    name="financial.annualExpenses"
                    value={formData.financial?.annualExpenses || 0}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="financial.profitMargin" className="block text-sm font-medium text-gray-700 mb-1">
                    Profit Margin (%)
                  </label>
                  <input
                    type="number"
                    id="financial.profitMargin"
                    name="financial.profitMargin"
                    value={formData.financial?.profitMargin || 0}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="financial.growthRate" className="block text-sm font-medium text-gray-700 mb-1">
                    Growth Rate (%)
                  </label>
                  <input
                    type="number"
                    id="financial.growthRate"
                    name="financial.growthRate"
                    value={formData.financial?.growthRate || 0}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="financial.financingAvailable"
                    name="financial.financingAvailable"
                    checked={formData.financial?.financingAvailable || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="financial.financingAvailable" className="ml-2 block text-sm text-gray-900">
                    Financing Available
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per Revenue
                  </label>
                  <input
                    type="text"
                    value={formData.financial?.pricePerRevenue?.toFixed(2) || '0.00'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per Cash Flow
                  </label>
                  <input
                    type="text"
                    value={formData.financial?.pricePerCashFlow?.toFixed(2) || '0.00'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="business.established" className="block text-sm font-medium text-gray-700 mb-1">
                    Year Established *
                  </label>
                  <input
                    type="number"
                    id="business.established"
                    name="business.established"
                    value={formData.business?.established || new Date().getFullYear()}
                    onChange={handleInputChange}
                    required
                    min="1900"
                    max={new Date().getFullYear()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employees (Min - Max) *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      id="business.employees.min"
                      name="business.employees.min"
                      value={formData.business?.employees?.min || 0}
                      onChange={handleInputChange}
                      required
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input
                      type="number"
                      id="business.employees.max"
                      name="business.employees.max"
                      value={formData.business?.employees?.max || 0}
                      onChange={handleInputChange}
                      required
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="business.squareFootage" className="block text-sm font-medium text-gray-700 mb-1">
                    Square Footage
                  </label>
                  <input
                    type="number"
                    id="business.squareFootage"
                    name="business.squareFootage"
                    value={formData.business?.squareFootage || 0}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="business.leaseTerms" className="block text-sm font-medium text-gray-700 mb-1">
                    Lease Terms
                  </label>
                  <input
                    type="text"
                    id="business.leaseTerms"
                    name="business.leaseTerms"
                    value={formData.business?.leaseTerms || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="business.reasonForSale" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Sale
                  </label>
                  <textarea
                    id="business.reasonForSale"
                    name="business.reasonForSale"
                    value={formData.business?.reasonForSale || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="business.trainingIncluded"
                        name="business.trainingIncluded"
                        checked={formData.business?.trainingIncluded || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="business.trainingIncluded" className="ml-2 block text-sm text-gray-900">
                        Training Included
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="business.relocatable"
                        name="business.relocatable"
                        checked={formData.business?.relocatable || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="business.relocatable" className="ml-2 block text-sm text-gray-900">
                        Relocatable
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="business.workFromHome"
                        name="business.workFromHome"
                        checked={formData.business?.workFromHome || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="business.workFromHome" className="ml-2 block text-sm text-gray-900">
                        Work From Home
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="business.absenteeOwnership"
                        name="business.absenteeOwnership"
                        checked={formData.business?.absenteeOwnership || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="business.absenteeOwnership" className="ml-2 block text-sm text-gray-900">
                        Absentee Ownership
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="business.franchise"
                        name="business.franchise"
                        checked={formData.business?.franchise || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="business.franchise" className="ml-2 block text-sm text-gray-900">
                        Franchise
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Media */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Media</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="media.featuredImage" className="block text-sm font-medium text-gray-700 mb-1">
                    Featured Image URL
                  </label>
                  <input
                    type="url"
                    id="media.featuredImage"
                    name="media.featuredImage"
                    value={formData.media?.featuredImage || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Images (URLs)
                  </label>
                  <input
                    type="url"
                    placeholder="Press Enter to add an image URL"
                    onKeyDown={(e) => handleMediaInput('images', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="mt-2 space-y-1">
                    {formData.media?.images?.map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded">
                        <span className="text-sm truncate">{url}</span>
                        <button
                          type="button"
                          onClick={() => removeMediaItem('images', url)}
                          className="text-red-600 hover:text-red-800 ml-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Videos (URLs)
                  </label>
                  <input
                    type="url"
                    placeholder="Press Enter to add a video URL"
                    onKeyDown={(e) => handleMediaInput('videos', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="mt-2 space-y-1">
                    {formData.media?.videos?.map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded">
                        <span className="text-sm truncate">{url}</span>
                        <button
                          type="button"
                          onClick={() => removeMediaItem('videos', url)}
                          className="text-red-600 hover:text-red-800 ml-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documents (URLs)
                  </label>
                  <input
                    type="url"
                    placeholder="Press Enter to add a document URL"
                    onKeyDown={(e) => handleMediaInput('documents', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="mt-2 space-y-1">
                    {formData.media?.documents?.map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded">
                        <span className="text-sm truncate">{url}</span>
                        <button
                          type="button"
                          onClick={() => removeMediaItem('documents', url)}
                          className="text-red-600 hover:text-red-800 ml-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Owner Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="owner.userId" className="block text-sm font-medium text-gray-700 mb-1">
                    User ID *
                  </label>
                  <input
                    type="text"
                    id="owner.userId"
                    name="owner.userId"
                    value={formData.owner?.userId || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="owner.brokerId" className="block text-sm font-medium text-gray-700 mb-1">
                    Broker ID
                  </label>
                  <input
                    type="text"
                    id="owner.brokerId"
                    name="owner.brokerId"
                    value={formData.owner?.brokerId || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="owner.contactInfo.name" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    id="owner.contactInfo.name"
                    name="owner.contactInfo.name"
                    value={formData.owner?.contactInfo?.name || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="owner.contactInfo.email" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    id="owner.contactInfo.email"
                    name="owner.contactInfo.email"
                    value={formData.owner?.contactInfo?.email || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="owner.contactInfo.phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone *
                  </label>
                  <input
                    type="tel"
                    id="owner.contactInfo.phone"
                    name="owner.contactInfo.phone"
                    value={formData.owner?.contactInfo?.phone || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="owner.contactInfo.preferredContact" className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Contact *
                  </label>
                  <select
                    id="owner.contactInfo.preferredContact"
                    name="owner.contactInfo.preferredContact"
                    value={formData.owner?.contactInfo?.preferredContact || 'email'}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="owner.responseTime" className="block text-sm font-medium text-gray-700 mb-1">
                    Response Time (hours)
                  </label>
                  <input
                    type="number"
                    id="owner.responseTime"
                    name="owner.responseTime"
                    value={formData.owner?.responseTime || 24}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Timestamps (Read-only) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps (Read-only)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created At
                  </label>
                  <input
                    type="text"
                    value={formatTimestamp(listing.basic.createdAt)}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Updated At
                  </label>
                  <input
                    type="text"
                    value={formatTimestamp(listing.basic.updatedAt)}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires At
                  </label>
                  <input
                    type="text"
                    value={formatTimestamp(listing.basic.expiresAt)}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Published At
                  </label>
                  <input
                    type="text"
                    value={formatTimestamp(listing.basic.publishedAt)}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-4">
              <Link
                to={listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-6 py-2 rounded-md font-medium transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AdminListingEdit;
